import React, { useState } from 'react';
import { Box, Typography, TextField, Grid, Select, MenuItem, FormControl, Button, Alert, Snackbar } from '@mui/material';
import { ArrowForward as ArrowIcon } from '@mui/icons-material';

function ContactForm({ courseName, onSubmit }) {
  const [formData, setFormData] = useState({
    name: '',
    identity: '',
    course: courseName || '',
    phone: '',
    email: '',
    note: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // 使用FormData发送到服务器端处理
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('identity', formData.identity);
      formDataToSend.append('course', formData.course);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('message', formData.note);
      formDataToSend.append('to_email', 'info@chihuimandarin.com');

      // 发送到服务器端PHP脚本
      // 在开发环境中使用完整URL，在生产环境中使用相对路径
      const emailUrl = process.env.NODE_ENV === 'development' 
        ? 'https://chihuimandarin.com/send-email.php'
        : '/send-email.php';
      
      const response = await fetch(emailUrl, {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          showSnackbar('Message sent successfully! We will contact you soon.', 'success');
        } else {
          showSnackbar(result.error || 'Failed to send message. Please try again.', 'error');
        }
        
        // 只有成功时才重置表单
        if (result.success) {
          setFormData({
            name: '',
            identity: '',
            course: courseName || '',
            phone: '',
            email: '',
            note: ''
          });
          
          // 调用回调函数（如果提供）
          if (onSubmit) {
            onSubmit();
          }
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Server error:', response.status, errorData);
        showSnackbar(`Failed to send message. Error: ${response.status}. Please try again or contact us directly.`, 'error');
      }

    } catch (error) {
      console.error('Email sending failed:', error);
      showSnackbar('Failed to send message. Please check your internet connection and try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: '800px', mx: 'auto' }}>
      {/* Name and Identity Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="body2" sx={{ color: '#07284b', fontWeight: 600, mb: 1 }}>
            Name *
          </Typography>
          <TextField
            name="name"
            variant="outlined"
            fullWidth
            value={formData.name}
            onChange={handleInputChange}
            required
            placeholder="Enter your full name"
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                borderRadius: 2,
                '& fieldset': {
                  borderColor: 'rgba(7, 40, 75, 0.2)'
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(7, 40, 75, 0.4)'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#9f2122'
                }
              }
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="body2" sx={{ color: '#07284b', fontWeight: 600, mb: 1 }}>
            Identity *
          </Typography>
          <FormControl fullWidth>
            <Select
              name="identity"
              value={formData.identity}
              onChange={handleInputChange}
              required
              displayEmpty
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(7, 40, 75, 0.2)'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(7, 40, 75, 0.4)'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#9f2122'
                }
              }}
            >
              <MenuItem value="" disabled>
                <em>Select your identity</em>
              </MenuItem>
              <MenuItem value="pre-k">pre-k</MenuItem>
              <MenuItem value="primary-secondary">Primary & Secondary Students</MenuItem>
              <MenuItem value="other-schools">Other schools or institutions</MenuItem>
              <MenuItem value="university">University Students</MenuItem>
              <MenuItem value="adult">Adults</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Course Selection Row */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ color: '#07284b', fontWeight: 600, mb: 1 }}>
          Course Interest
        </Typography>
        <FormControl fullWidth>
          <Select
            name="course"
            value={formData.course}
            onChange={handleInputChange}
            displayEmpty
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.8)',
              borderRadius: 2,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(7, 40, 75, 0.2)'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(7, 40, 75, 0.4)'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#9f2122'
              }
            }}
          >
            <MenuItem value="" disabled>
              <em>Select a course you're interested in</em>
            </MenuItem>
            <MenuItem value="business-chinese">Business Chinese</MenuItem>
            <MenuItem value="exam-preparation">Exam Preparation</MenuItem>
            <MenuItem value="speech-eloquence">Speech & Eloquence</MenuItem>
            <MenuItem value="immersion-programs">Immersion Programs</MenuItem>
            <MenuItem value="private-customization">Tailored Programs</MenuItem>
            <MenuItem value="not-sure">Not sure yet</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Phone and Email Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="body2" sx={{ color: '#07284b', fontWeight: 600, mb: 1 }}>
            Phone *
          </Typography>
          <TextField
            name="phone"
            variant="outlined"
            fullWidth
            value={formData.phone}
            onChange={handleInputChange}
            required
            placeholder="Enter your phone number"
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                borderRadius: 2,
                '& fieldset': {
                  borderColor: 'rgba(7, 40, 75, 0.2)'
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(7, 40, 75, 0.4)'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#9f2122'
                }
              }
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="body2" sx={{ color: '#07284b', fontWeight: 600, mb: 1 }}>
            Email *
          </Typography>
          <TextField
            name="email"
            variant="outlined"
            fullWidth
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            placeholder="Enter your email address"
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                borderRadius: 2,
                '& fieldset': {
                  borderColor: 'rgba(7, 40, 75, 0.2)'
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(7, 40, 75, 0.4)'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#9f2122'
                }
              }
            }}
          />
        </Grid>
      </Grid>

      {/* Note Field */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ color: '#07284b', fontWeight: 600, mb: 1 }}>
          Message
        </Typography>
        <TextField
          name="note"
          variant="outlined"
          fullWidth
          multiline
          rows={4}
          value={formData.note}
          onChange={handleInputChange}
          placeholder="Tell us about your learning goals or any questions you have..."
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'rgba(255, 255, 255, 0.8)',
              borderRadius: 2,
              '& fieldset': {
                borderColor: 'rgba(7, 40, 75, 0.2)'
              },
              '&:hover fieldset': {
                borderColor: 'rgba(7, 40, 75, 0.4)'
              },
              '&.Mui-focused fieldset': {
                borderColor: '#9f2122'
              }
            }
          }}
        />
      </Box>

      {/* Submit Button */}
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          endIcon={!isSubmitting && <ArrowIcon />}
          sx={{
            bgcolor: '#9f2122',
            color: 'white',
            py: 2,
            px: 6,
            fontSize: '1.1rem',
            fontWeight: 700,
            borderRadius: 3,
            textTransform: 'none',
            boxShadow: '0 4px 20px rgba(159, 33, 34, 0.3)',
            '&:hover': {
              bgcolor: '#8a1e1f',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 25px rgba(159, 33, 34, 0.4)'
            },
            '&:disabled': {
              bgcolor: 'rgba(159, 33, 34, 0.6)',
              color: 'rgba(255, 255, 255, 0.7)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </Button>
      </Box>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            '& .MuiAlert-message': {
              fontSize: '1rem',
              fontWeight: 500
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ContactForm;
